import React, { useState, useRef, useEffect } from 'react';
import { Users as UsersIcon, Plus, Minus } from 'lucide-react';

/**
 * Compact Adults + Children popover picker.
 * Children are defined as age < 15.
 *
 * Props:
 *  - adults: number
 *  - children: number
 *  - onChange: ({adults, children}) => void
 *  - testIdPrefix: e.g. "hero" or "booking"
 *  - compact: boolean — true uses inline summary (no label column)
 */
export const GuestPicker = ({ adults, children, onChange, testIdPrefix = 'guest', compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const bump = (key, delta, min, max) => {
    const next = { adults, children, [key]: Math.max(min, Math.min(max, (key === 'adults' ? adults : children) + delta)) };
    onChange({ adults: next.adults, children: next.children });
  };

  const summary = `${adults} Adult${adults !== 1 ? 's' : ''} · ${children} Child${children !== 1 ? 'ren' : ''}`;

  return (
    <div ref={ref} className="relative" data-testid={`${testIdPrefix}-picker`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full text-left ${compact ? 'text-sm font-bold text-black' : 'text-base font-bold text-black'} focus:outline-none flex items-center gap-1.5`}
        data-testid={`${testIdPrefix}-picker-btn`}
      >
        {!compact && <UsersIcon className="h-3.5 w-3.5 text-gray-500" />}
        <span className="truncate">{summary}</span>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-2 left-0 sm:right-0 sm:left-auto w-72 bg-white rounded-2xl shadow-xl border border-gray-200 p-4"
          data-testid={`${testIdPrefix}-picker-popover`}
        >
          <Row
            label="Adults"
            hint="15 yrs and above"
            value={adults}
            min={1}
            max={10}
            onMinus={() => bump('adults', -1, 1, 10)}
            onPlus={() => bump('adults', 1, 1, 10)}
            testIdPrefix={`${testIdPrefix}-adults`}
          />
          <div className="border-t border-gray-100 my-3" />
          <Row
            label="Children"
            hint="Below 15 yrs"
            value={children}
            min={0}
            max={6}
            onMinus={() => bump('children', -1, 0, 6)}
            onPlus={() => bump('children', 1, 0, 6)}
            testIdPrefix={`${testIdPrefix}-children`}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-4 w-full px-3 py-2 rounded-full bg-black text-white text-xs font-bold hover:bg-[#FF0059] transition-colors"
            data-testid={`${testIdPrefix}-picker-done`}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, hint, value, min, max, onMinus, onPlus, testIdPrefix }) => (
  <div className="flex items-center justify-between gap-3">
    <div>
      <p className="text-sm font-bold text-black">{label}</p>
      <p className="text-[11px] text-gray-500">{hint}</p>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onMinus}
        disabled={value <= min}
        className="w-8 h-8 rounded-full border-2 border-gray-300 hover:border-[#FF0059] hover:text-[#FF0059] disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:text-current flex items-center justify-center transition-colors"
        aria-label={`Decrease ${label}`}
        data-testid={`${testIdPrefix}-minus`}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-6 text-center font-black text-base" data-testid={`${testIdPrefix}-value`}>{value}</span>
      <button
        type="button"
        onClick={onPlus}
        disabled={value >= max}
        className="w-8 h-8 rounded-full border-2 border-gray-300 hover:border-[#FF0059] hover:text-[#FF0059] disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:text-current flex items-center justify-center transition-colors"
        aria-label={`Increase ${label}`}
        data-testid={`${testIdPrefix}-plus`}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);
